import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { PaginatedResponse } from '@app/core/models/shared/admin.model';
import {
  ClientRegistryItem,
  ClientsPageFilter,
  UserDetailResponse,
} from '@app/core/models/client/client-registry.model';

const EMPTY_PAGE: PaginatedResponse<ClientRegistryItem> = {
  currentPageNumber: 0,
  pageSize: 0,
  totalElements: 0,
  totalPages: 0,
  isLastPage: true,
  isFirstPage: true,
  isEmpty: true,
  content: [],
};

@Injectable({
  providedIn: 'root',
})
export class AdminClientsService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  getClientsPage(
    page: number,
    size: number,
    filter: ClientsPageFilter
  ): Observable<PaginatedResponse<ClientRegistryItem>> {
    this.isLoading.set(true);

    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('search', filter.search ?? '')
      .set('minTotalCertificates', filter.minTotalCertificates != null ? String(filter.minTotalCertificates) : '')
      .set('maxTotalCertificates', filter.maxTotalCertificates != null ? String(filter.maxTotalCertificates) : '')
      .set('minActiveCertificates', filter.minActiveCertificates != null ? String(filter.minActiveCertificates) : '')
      .set('maxActiveCertificates', filter.maxActiveCertificates != null ? String(filter.maxActiveCertificates) : '');

    return this.http
      .get<PaginatedResponse<ClientRegistryItem>>(`${this.apiBaseUrl}/admin/users`, {
        params,
      })
      .pipe(
        catchError(error => {
          console.error('Ошибка при получении списка клиентов:', error);
          this.translate.get('Ошибка при получении списка клиентов').subscribe(translation => {
            this.snackbarService.error(translation);
          });
          return of(EMPTY_PAGE);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  getUserDetail(userId: string): Observable<UserDetailResponse> {
    this.isLoading.set(true);
    const params = new HttpParams().set('userId', userId);
    return this.http
      .get<UserDetailResponse>(`${this.apiBaseUrl}/admin/user-detail`, { params })
      .pipe(
        catchError(error => {
          console.error('Ошибка при получении деталей клиента:', error);
          this.translate.get('Ошибка при получении деталей клиента').subscribe(translation => {
            this.snackbarService.error(translation);
          });
          throw error;
        }),
        finalize(() => this.isLoading.set(false))
      );
  }
}
